import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, Calendar, Users, Tag, ArrowLeft, Award, Star, Trophy, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

export default function PaperDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);
  const [authorDetails, setAuthorDetails] = useState([]);
  const [relatedSchemes, setRelatedSchemes] = useState([]);

  useEffect(() => {
    fetchPaperDetail();
    fetchRelatedSchemes();
    if (user) {
      checkAccess();
    }
  }, [id, user]);

  const fetchRelatedSchemes = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/papers/${id}/related-schemes`);
      setRelatedSchemes(data.related_schemes || []);
    } catch (error) {
      console.error('Error fetching related schemes:', error);
    }
  };

  const fetchPaperDetail = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/papers/${id}`
      );
      setPaper(data);
      
      // Fetch author details if author_ids exist
      if (data.author_ids && data.author_ids.length > 0) {
        fetchAuthors(data.author_ids);
      }
    } catch (error) {
      console.error('Error fetching paper:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthors = async (authorIds) => {
    try {
      const promises = authorIds.map(authorId =>
        axios.get(`${API_BASE_URL}/api/authors/${authorId}`)
          .catch(err => null) // Handle individual author fetch failures
      );
      const results = await Promise.all(promises);
      const validAuthors = results.filter(res => res !== null).map(res => res.data);
      setAuthorDetails(validAuthors);
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const checkAccess = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/papers/${id}/access-status`,
        { withCredentials: true }
      );
      setHasAccess(data.has_access);
      setAccessStatus(data);
    } catch (error) {
      console.error('Error checking access:', error);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/papers/${id}/download`,
        { 
          withCredentials: true,
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${paper.title}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 403) {
        alert('You have not purchased this paper yet.');
      } else if (error.response?.status === 404) {
        alert('PDF file not found. Please contact support.');
      } else {
        alert('Download failed. Please try again or contact support.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">Paper not found</p>
          <Button onClick={() => navigate('/research-library')} className="mt-4">
            Back to Platform
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/research-library')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Platform
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card className="p-8">
              {/* Badges Row */}
              <div className="flex flex-wrap gap-2 mb-4">
                {/* Pillar Badge */}
                <span className="inline-block px-4 py-2 bg-[#1E3A5F] text-white text-sm rounded-full">
                  {paper.pillar}
                </span>
                
                {/* Rating Band Badge */}
                {paper.rating_band && (
                  <>
                    {paper.rating_band === 'flagship' && (
                      <span className="inline-flex items-center gap-1 px-4 py-2 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                        <Trophy className="h-4 w-4" />
                        Flagship
                      </span>
                    )}
                    {paper.rating_band === 'strong' && (
                      <span className="inline-flex items-center gap-1 px-4 py-2 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                        <Star className="h-4 w-4" />
                        Strong
                      </span>
                    )}
                    {paper.rating_band === 'moderate' && (
                      <span className="inline-flex items-center gap-1 px-4 py-2 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                        <CheckCircle className="h-4 w-4" />
                        Moderate
                      </span>
                    )}
                  </>
                )}
                
                {/* Expert Reviewed Badge */}
                {paper.expert_reviewed && (
                  <span className="inline-flex items-center gap-1 px-4 py-2 bg-purple-100 text-purple-800 text-sm font-semibold rounded-full">
                    <Award className="h-4 w-4" />
                    Expert Reviewed
                  </span>
                )}
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {paper.title}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap gap-4 mb-6 text-gray-600 text-sm">
                {paper.publication_date && (
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    {new Date(paper.publication_date).toLocaleDateString()}
                  </div>
                )}
                <div className="flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  {paper.downloads} downloads
                </div>
                {paper.total_score !== undefined && (
                  <div className="flex items-center font-semibold text-[#E87722]">
                    <Award className="h-4 w-4 mr-2" />
                    Quality Score: {paper.total_score}/30
                  </div>
                )}
              </div>

              {/* Abstract */}
              {paper.abstract && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h2 className="text-lg font-semibold text-[#1E3A5F] mb-2">Abstract</h2>
                  <p className="text-gray-700 leading-relaxed">{paper.abstract}</p>
                </div>
              )}

              {/* Review Scores Breakdown */}
              {paper.review_scores && (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h2 className="text-lg font-semibold text-[#1E3A5F] mb-3">Quality Assessment</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white rounded border">
                      <div className="text-2xl font-bold text-[#E87722]">
                        {paper.review_scores.relevance}/10
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Relevance to Pillars</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                      <div className="text-2xl font-bold text-[#E87722]">
                        {paper.review_scores.analytical_depth}/10
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Analytical Depth</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                      <div className="text-2xl font-bold text-[#E87722]">
                        {paper.review_scores.practical_applicability}/10
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Practical Applicability</div>
                    </div>
                  </div>
                  {paper.review_summary && (
                    <div className="mt-4 p-3 bg-white rounded border">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Review Summary</h3>
                      <p className="text-sm text-gray-700">{paper.review_summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              <div className="prose max-w-none mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Description</h2>
                <p className="text-gray-700 whitespace-pre-line">{paper.description}</p>
              </div>

              {/* Theme */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <Tag className="h-5 w-5 mr-2 text-[#E87722]" />
                  Theme
                </h3>
                <p className="text-gray-700">{paper.theme}</p>
              </div>

              {/* Sub-themes */}
              {paper.sub_themes && paper.sub_themes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sub-themes</h3>
                  <div className="flex flex-wrap gap-2">
                    {paper.sub_themes.map((subTheme, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {subTheme}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Authors with Profiles */}
              {authorDetails.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-[#E87722]" />
                    Authors
                  </h3>
                  <div className="space-y-4">
                    {authorDetails.map((author) => (
                      <div key={author.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="font-semibold text-gray-900 text-lg">{author.name}</div>
                        <div className="text-sm text-[#E87722] mb-2">{author.credentials}</div>
                        <div className="text-sm text-gray-600 mb-2">{author.affiliation}</div>
                        <p className="text-sm text-gray-700">{author.bio}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fallback: Show authors from paper if author details not loaded */}
              {authorDetails.length === 0 && paper.authors && paper.authors.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-[#E87722]" />
                    Authors
                  </h3>
                  <p className="text-gray-700">{paper.authors.join(', ')}</p>
                </div>
              )}

              {/* Keywords */}
              {paper.keywords && paper.keywords.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Keywords</h3>
                  <div className="flex flex-wrap gap-2">
                    {paper.keywords.map((keyword, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-[#1E3A5F] text-white text-sm rounded"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Related Government Schemes */}
            {relatedSchemes.length > 0 && (
              <Card className="p-8 mt-6" data-testid="related-schemes-card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Government Schemes</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {relatedSchemes.map((scheme) => (
                    <button
                      key={scheme.id}
                      onClick={() => navigate(`/programs/${scheme.id}`)}
                      className="text-left p-4 border rounded-lg hover:border-[#E87722] hover:bg-orange-50/50 transition-colors"
                      data-testid={`related-scheme-${scheme.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-semibold text-sm text-[#1E3A5F] line-clamp-2">{scheme.scheme_name}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${scheme.link_type === 'manual' ? 'border-[#E87722] text-[#E87722]' : 'border-gray-300 text-gray-500'}`}>
                          {scheme.link_type === 'manual' ? 'Curated' : 'Suggested'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{scheme.ministry_department} • {scheme.government_level} Government</p>
                      <p className="text-xs text-gray-600 mt-2 line-clamp-2">{scheme.objective_summary}</p>
                    </button>
                  ))}
                </div>
              </Card>
            )}
          </div>
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-4" data-testid="paper-access-card">
              {hasAccess ? (
                <>
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-sm text-green-800 font-medium">
                      <CheckCircle className="h-4 w-4 inline mr-1" />Member Access
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Unlimited access as a verified CSED member
                    </p>
                  </div>
                  <Button 
                    onClick={handleDownload}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    data-testid="paper-download-btn"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Download Paper
                  </Button>
                </>
              ) : (
                <div data-testid="members-only-prompt">
                  <div className="text-center mb-5">
                    <div className="mx-auto h-14 w-14 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mb-3">
                      <Lock className="h-7 w-7 text-[#1E3A5F]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1E3A5F]">Members Only</h3>
                  </div>
                  <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      This knowledge resource has been carefully researched and curated with significant effort.
                      Full access to the CSED Research Library is an exclusive benefit of CSED membership.
                    </p>
                  </div>
                  {user && (
                    <p className="text-xs text-gray-500 text-center mb-4">
                      Already applied? Access opens automatically once your membership is approved.
                    </p>
                  )}
                  <Button
                    onClick={() => navigate('/membership')}
                    className="w-full bg-[#E87722] hover:bg-[#d66a1a] text-white"
                    data-testid="join-csed-btn"
                  >
                    Join CSED — View Membership Plans
                  </Button>
                  {!user && (
                    <Button
                      variant="outline"
                      onClick={() => navigate('/login')}
                      className="w-full mt-3"
                      data-testid="member-login-btn"
                    >
                      Already a member? Log in
                    </Button>
                  )}
                </div>
              )}

              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold text-gray-900 mb-3">Membership Includes</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="text-[#E87722] mr-2">✓</span>
                    Unlimited access to the full Research Library
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#E87722] mr-2">✓</span>
                    Government Schemes Platform
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#E87722] mr-2">✓</span>
                    Member directory & network
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
